/** @jsx jsx */
import React, { useCallback, useMemo, useRef, useLayoutEffect, type RefCallback } from 'react';
import { css, jsx } from '@compiled/react';
import isNil from 'lodash/isNil';
import { graphql, useFragment } from 'react-relay';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import { Text, Inline, xcss, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { SpotlightManager, SpotlightTarget } from '@atlaskit/onboarding';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIntl } from '@atlassian/jira-intl';
import {
	GOALS_CF_TYPE,
	STATUS_TYPE,
	NUMBER_CF_TYPE,
	PRIORITY_TYPE,
	CATEGORY_TYPE,
	ASSIGNEE_TYPE,
	SPRINT_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import type {
	groupHeader_issueTableGrouping$key,
	groupHeader_issueTableGrouping$data,
} from '@atlassian/jira-relay/src/__generated__/groupHeader_issueTableGrouping.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useFlatList,
	useIsItemExpanded,
	useIsItemLoading,
	useIsSelected,
} from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import {
	ExpandButtonWithCreateChildButton,
	ExpandButton,
} from '@atlassian/jira-issue-table-hierarchy/src/ui/expand-button/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import CreateChildButton from '@atlassian/jira-issue-table-hierarchy/src/ui/create-child-button/index.tsx';
import { isCreateIssueSupportedForFieldType } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/index.tsx';

import { AssigneeGroupHeader } from './assignee-group-header/index.tsx';
import { CategoryGroupHeader } from './category-group-header/index.tsx';
import messages from './messages.tsx';
import { PriorityGroupHeader } from './priority-group-header/index.tsx';
import { SprintGroupHeader } from './sprint-group-header/index.tsx';
import { StatusGroupHeader } from './status-group-header/index.tsx';
import { StoryPointsGroupHeader } from './story-points-group-header/index.tsx';
import { GoalGroupHeader } from './goal-group-header/index.tsx';

export type Props = {
	groupHeader: groupHeader_issueTableGrouping$key;
	groupConnectionId: string;
	isGroupAtTop?: boolean;
	columnsCount: number;
	groupId?: string;
	createChildTriggerProps?: {
		onClick: () => void;
		ref: RefCallback<HTMLElement>;
	};
	isIssueCreateEnabled?: boolean;
};

const isCompletedSprintGroup = (data: groupHeader_issueTableGrouping$data) =>
	data?.fieldValue?.__typename === 'JiraJqlSprintFieldValue' &&
	data.fieldValue.sprint?.state === 'CLOSED';

const ONBOARDING_TARGET_NAME = 'issue-navigator-create-issue-in-group';

export const GroupHeader = ({
	groupHeader,
	groupConnectionId,
	createChildTriggerProps,
	isGroupAtTop = false,
	columnsCount,
	groupId,
	isIssueCreateEnabled = true,
}: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const tableRowRef = useRef<HTMLTableRowElement>(null);

	const data = useFragment<groupHeader_issueTableGrouping$key>(
		graphql`
			fragment groupHeader_issueTableGrouping on JiraSpreadsheetGroup {
				id
				fieldType
				issueCount
				fieldValue {
					__typename
					...priorityGroupHeader_issueTableGrouping
					...assigneeGroupHeader_issueTableGrouping
					...statusGroupHeader_issueTableGrouping
					...categoryGroupHeader_issueTableGrouping
					...sprintGroupHeader_issueTableGrouping
					...storyPointsGroupHeader_issueTableGrouping
					...goalGroupHeader_issueTableGrouping
					... on JiraJqlSprintFieldValue {
						sprint {
							state
						}
					}
				}
			}
		`,
		groupHeader,
	);

	const itemDetails = useMemo(
		() => (data ? { itemsConnectionId: groupConnectionId, itemId: data.id, groupId } : null),
		[data, groupConnectionId, groupId],
	);

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const list = fg('nin-grid-keyboard-navigation') ? useFlatList() : [];

	const [isSelected, { setSelectedRow }] = useIsSelected(itemDetails);

	useLayoutEffect(() => {
		// Scroll the table row into view (if needed - block: nearest) when it becomes selected
		if (isSelected) {
			tableRowRef.current?.scrollIntoView({ block: 'nearest' });
		}
	}, [isSelected]);

	const focusCurrentRow = useCallback(() => {
		return setSelectedRow(itemDetails);
	}, [setSelectedRow, itemDetails]);

	const groupHeaderView = useMemo(() => {
		switch (data.fieldType) {
			case PRIORITY_TYPE:
				return <PriorityGroupHeader priorityGroupHeader={data.fieldValue ?? null} />;
			case ASSIGNEE_TYPE:
				return <AssigneeGroupHeader assigneeGroupHeader={data.fieldValue ?? null} />;
			case STATUS_TYPE:
				return <StatusGroupHeader statusGroupHeader={data.fieldValue ?? null} />;
			case NUMBER_CF_TYPE:
			case STORY_POINT_ESTIMATE_CF_TYPE:
				return <StoryPointsGroupHeader storyPointsGroupHeader={data.fieldValue ?? null} />;
			case GOALS_CF_TYPE:
				return <GoalGroupHeader goalGroupHeader={data.fieldValue ?? null} />;
			case CATEGORY_TYPE:
				return <CategoryGroupHeader categoryGroupHeader={data.fieldValue ?? null} />;
			case SPRINT_TYPE:
				return <SprintGroupHeader sprintGroupHeader={data.fieldValue ?? null} />;
			default:
				// should never happen
				return null;
		}
	}, [data]);

	const groupMetadata = useMemo(() => {
		return (
			<>
				{groupHeaderView}
				{!isNil(data?.issueCount) && (
					<Text color="color.text.subtlest">
						{formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.issuesIssueTermRefresh
								: messages.issues,
							{ count: data.issueCount },
						)}
					</Text>
				)}
			</>
		);
	}, [data.issueCount, formatMessage, groupHeaderView]);

	const [isExpanded, { toggleIsItemExpanded }] = useIsItemExpanded(itemDetails);
	const [isLoading] = useIsItemLoading(data?.id ?? null);

	const onCreateChildClick = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'groupCreateChildButton');
		if (!isExpanded) {
			toggleIsItemExpanded(itemDetails, 0);
		}
		createChildTriggerProps?.onClick();
	}, [
		createAnalyticsEvent,
		isExpanded,
		toggleIsItemExpanded,
		itemDetails,
		createChildTriggerProps,
	]);

	const headerContent = useMemo(() => {
		if (expVal('jira_spreadsheet_issue_create_in_group', 'isEnabled', false)) {
			return (
				<>
					{data.issueCount ? (
						<ExpandButton
							itemId={data.id}
							itemsConnectionId={groupConnectionId}
							depth={0}
							isGroupButton
							groupId={groupId}
						/>
					) : null}
					{groupMetadata}
					{isIssueCreateEnabled &&
						isCreateIssueSupportedForFieldType(data.fieldType) &&
						!isCompletedSprintGroup(data) &&
						(isGroupAtTop ? (
							<SpotlightManager>
								<SpotlightTarget name={ONBOARDING_TARGET_NAME}>
									<div css={raiseOnHoverStyles}>
										<CreateChildButton
											onClick={onCreateChildClick}
											ref={createChildTriggerProps?.ref}
											isCreateWorkItem
										/>
									</div>
								</SpotlightTarget>
								<EngagementSpotlight engagementId={ONBOARDING_TARGET_NAME} />
							</SpotlightManager>
						) : (
							<div css={raiseOnHoverStyles}>
								<CreateChildButton
									onClick={onCreateChildClick}
									ref={createChildTriggerProps?.ref}
									isCreateWorkItem
								/>
							</div>
						))}
				</>
			);
		}

		return (
			<>
				{data.issueCount ? (
					<ExpandButtonWithCreateChildButton
						isGroupButton
						itemId={data.id}
						itemsConnectionId={groupConnectionId}
						depth={0}
						groupId={groupId}
					/>
				) : null}
				{groupMetadata}
			</>
		);
	}, [
		data,
		groupConnectionId,
		groupId,
		groupMetadata,
		isIssueCreateEnabled,
		isGroupAtTop,
		onCreateChildClick,
		createChildTriggerProps?.ref,
	]);

	const rowIndex =
		itemDetails && fg('nin-grid-keyboard-navigation')
			? list.indexOf(`${itemDetails.itemId}::${itemDetails.itemId}`) + 1
			: -1;
	return (
		<tr
			role="row"
			tabIndex={-1}
			{...(rowIndex > 0 &&
				fg('nin-grid-keyboard-navigation') && {
					'aria-rowindex': rowIndex,
				})}
			css={[
				headerRowStyles,
				isSelected && !fg('nin-grid-keyboard-navigation') && activeRowStyles,
				fg('nin-grid-keyboard-navigation') && headerRowKeyboardNavigationStyles,
			]}
			onFocus={focusCurrentRow}
			data-testid="issue-table-grouping.ui.group-row.group-header.tr"
			aria-selected={isSelected}
			ref={tableRowRef}
		>
			<Box
				as="td"
				// Additional column for configure columns menu & issue actions
				colSpan={columnsCount + 1}
				xcss={[
					borderStyles,
					isGroupAtTop && removeTopBorder,
					(!isExpanded ||
						isLoading ||
						!data.issueCount ||
						expVal('jira_spreadsheet_issue_create_in_group', 'isEnabled', false)) &&
						removeBottomBorder,
				]}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...(fg('nin-grid-keyboard-navigation') && { tabIndex: -1 })}
			>
				<Inline xcss={[inlineStyles, !data.issueCount && placeholderStyles]} space="space.100">
					{headerContent}
				</Inline>
			</Box>
		</tr>
	);
};

const inlineStyles = xcss({
	paddingInline: 'space.150',
	alignItems: 'center',
	gap: 'space.100',
});

const placeholderStyles = xcss({
	marginLeft: 'space.400',
});

const borderStyles = xcss({
	border: 0,
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderTopWidth: 'border.width',
	borderBottomWidth: 'border.width',
});

const removeTopBorder = xcss({
	borderTopWidth: '0',
});

const removeBottomBorder = xcss({
	borderBottomWidth: '0',
});

const headerRowStyles = css({
	'&:hover': {
		backgroundColor: token('elevation.surface.hovered'),
	},
	'&:focus': {
		outline: 'none',
	},
});

const headerRowKeyboardNavigationStyles = css({
	'&:focus-within': {
		backgroundColor: token('color.background.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
	},
});

const activeRowStyles = css({
	backgroundColor: token('color.background.selected'),
	'&:hover': {
		backgroundColor: token('color.background.selected.hovered'),
	},
});

const raiseOnHoverStyles = css({
	position: 'relative',
	'&:hover': {
		zIndex: '2',
	},
});
